import React, { useEffect, useRef, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import { getSessionDeadline, verifySession, expireSessionByTimer } from '../../lib/authGuard';
import './UnsavedChangesModal.css';

// =============================================
// SessionTimer — the browser counts the idle session down itself.
// =============================================
// Mounted once while signed in. The deadline comes from the server ("seconds left",
// refreshed by verifySession on activity) and is shared by all tabs. At 60s left it
// warns; at zero:
//  - if the moment was MISSED by minutes (PC asleep / tab frozen), nothing could have
//    kept the session alive, so sign out at once — no network wait;
//  - otherwise ask the server once (a long upload in the background keeps the session
//    alive server-side); a 401 or no answer within a few seconds = sign out.
// =============================================

const WARN_MS = 60 * 1000;
const MISSED_MS = 3 * 60 * 1000;     // hidden tabs may tick only once a minute
const CHECK_TIMEOUT_MS = 4000;

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p, new Promise<T>(r => setTimeout(() => r(fallback), ms))]);
}

const SessionTimer: React.FC = () => {
  const [left, setLeft] = useState<number | null>(null);   // ms left, only while warning
  const checking = useRef(false);

  useEffect(() => {
    const tick = async () => {
      if (checking.current) return;
      const deadline = getSessionDeadline();
      if (deadline == null) { setLeft(null); return; }
      const now = Date.now();
      const remaining = deadline - now;
      if (remaining > WARN_MS) { setLeft(null); return; }
      if (remaining > 0) { setLeft(remaining); return; }

      if (now - deadline > MISSED_MS) { expireSessionByTimer(); return; }
      checking.current = true;
      try {
        const before = getSessionDeadline();
        const ok = await withTimeout(verifySession(0), CHECK_TIMEOUT_MS, false);
        const after = getSessionDeadline();
        // Still signed in only if the server actually handed back a later deadline.
        if (!ok || after == null || before == null || after <= before) expireSessionByTimer();
      } finally {
        checking.current = false;
      }
    };
    void tick();
    const id = window.setInterval(() => { void tick(); }, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') void tick(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (left == null) return null;
  const secs = Math.max(0, Math.ceil(left / 1000));

  return (
    <div className="ucm-overlay">
      <div className="ucm-modal" role="alertdialog" aria-modal="true" aria-labelledby="st-title">
        <div className="ucm-icon"><FiClock size={22} /></div>
        <h3 id="st-title" className="ucm-title">Still there?</h3>
        <p className="ucm-body">
          You'll be signed out in <strong>{secs}s</strong> because there has been no activity.
        </p>
        <div className="ucm-actions">
          <button className="ucm-btn ucm-save" autoFocus onClick={() => { void verifySession(0); }}>
            Stay signed in
          </button>
          <button className="ucm-btn ucm-cancel" onClick={() => expireSessionByTimer()}>
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimer;
